import React, { useState, useEffect } from 'react';
import GoogleMapReact from 'google-map-react';
import { collection, query, getDocs, setDoc, doc } from 'firebase/firestore';
import { auth, db } from '../firebase/firebase';
import { useTranslation } from "react-i18next";
import { signInWithCustomToken } from "firebase/auth";
import { geocodeStoredAddress, shouldRefreshGeocode } from "./geocodeAddressUtils";

const MarkerComponent = ({ text }) => (
    <div style={{
        color: 'white',
        background: 'red',
        padding: '5px 5px',
        borderRadius: '50%',
        transform: 'translate(-50%, -50%)'
    }}>
        {text}
    </div>
);

const NewGoogleMapComponent = () => {
    const [markersData, setMarkersData] = useState([]);
    const [filter, setFilter] = useState('Alle');
    const [loading, setLoading] = useState(true);
    const { t } = useTranslation();
    const token = localStorage.getItem('token');

    const defaultProps = {
        center: {
            lat: 51.06571144325784,
            lng: 10.376118560697588
        },
        zoom: 5
    };

    useEffect(() => {
        const fetchAddresses = async () => {
            try {
                const customerAddressesQuery = query(collection(db, 'customerAddresses'));
                const customerAddressesSnapshot = await getDocs(customerAddressesQuery);

                if (customerAddressesSnapshot.empty) {
                    console.warn("No documents found in the customerAddresses collection.");
                    setLoading(false);
                    return;
                }

                const allAddresses = await Promise.all(
                    customerAddressesSnapshot.docs.map(async (docSnap) => {
                        const docId = docSnap.id;
                        const addressesCollectionRef = collection(db, `customerAddresses/${docId}/addresses`);
                        const addressesQuerySnapshot = await getDocs(addressesCollectionRef);

                        return addressesQuerySnapshot.docs.map(addressDoc => ({
                            ...addressDoc.data(),
                            docId: addressDoc.id,
                            parentDocId: docId,
                        }));
                    })
                );

                const flattenedAddresses = allAddresses.flat();

                const geocoder = new window.google.maps.Geocoder();

                const geocodedAddresses = [];

                for (const address of flattenedAddresses) {
                    if (!shouldRefreshGeocode(address)) {
                        geocodedAddresses.push(address);
                        continue;
                    }

                    const geocodedAddress = await geocodeStoredAddress(geocoder, address);

                    if (geocodedAddress) {
                        const updatedAddress = {
                            ...address,
                            ...geocodedAddress,
                        };

                        await setDoc(doc(db, `customerAddresses/${address.parentDocId}/addresses`, address.docId), updatedAddress);
                        geocodedAddresses.push(updatedAddress);
                        continue;
                    }

                    geocodedAddresses.push({ ...address, position: address.position || null });
                }

                setMarkersData(geocodedAddresses.filter(address => address.position));
                setLoading(false);
            } catch (error) {
                console.error("Error fetching addresses: ", error);
                setLoading(false);
            }
        };

        if (token) {
            signInWithCustomToken(auth, token)
                .then(() => {
                    fetchAddresses();
                })
                .catch(() => {
                    fetchAddresses();
                });
        } else {
            fetchAddresses();
        }
    }, [token]);

    const convertType = (type, filter) => {
        let convertedType = type;

        if (type === "Physio- & Ergotherapie" && filter === "Physiotherapie") {
            convertedType = "Physiotherapie";
        } else if (type === "Physio- & Ergotherapie" && filter === "Ergotherapie") {
            convertedType = "Ergotherapie";
        } else if ((type === "Rehaklinik" || type === "Akutklinik") && filter === "Klinik") {
            convertedType = "Klinik";
        }

        return convertedType;
    };

    const filteredMarkers = filter === 'Alle' ? markersData : markersData.filter(marker => {
        const convertedMarker = convertType(marker.typeOfIns, filter);
        return convertedMarker === filter;
    });

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div style={{ height: '700px', width: '100%' }}>
            <div className="flex justify-center mb-5">
                <div className="text-center">
                    <h1 className="text-xl font-bold mb-3">{t('filterByInstitutionType')}</h1>
                    <select
                        className="border border-gray-300 rounded-sm px-8 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600 shadow-sm"
                        onChange={(e) => setFilter(e.target.value)}
                        value={filter}
                    >
                        <option value="Alle">{t('Alle')}</option>
                        <option value="Physiotherapie">{t('Physiotherapie')}</option>
                        <option value="Ergotherapie">{t('Ergotherapie')}</option>
                        <option value="Klinik">{t('Klinik')}</option>
                        <option value="Sonstige">{t('Sonstige')}</option>
                    </select>
                </div>
            </div>
            <div>
                <GoogleMapReact
                    bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_MAP_API_KEY }}
                    defaultCenter={defaultProps.center}
                    defaultZoom={defaultProps.zoom}
                >
                    {filteredMarkers.map((marker, index) => (
                        <MarkerComponent
                            key={index}
                            lat={marker.position.lat}
                            lng={marker.position.lng}
                            text="📍"
                        />
                    ))}
                </GoogleMapReact>
            </div>
        </div>
    );
};

export default NewGoogleMapComponent;
